import { defaultHorseWeight, defaultRiderWeight } from '@/lib/constants'
import { average, parseNullableBoolean } from '@/lib/helpers/numeric'
import { secondsToHMS } from '@/lib/helpers/convert'
import { disciplinesAvailable } from '@/lib/constants'
import tailwind from '@/lib/tailwind'

const FURLONG_IN_METERS = 201.168
const MIN_SUSTAINED_CANTER_DURATION = 20
const toStrideTime = (value) => {
    if (value == null || (typeof value === 'string' && value.trim() === '')) {
        return NaN
    }

    const numericValue = Number(value)
    return Number.isFinite(numericValue) ? numericValue : NaN
}

const isWeirdSegment = (segment) => parseNullableBoolean(segment?.weird) === true
const isAnalyticsStride = (stride) => !isWeirdSegment(stride)
const isAnalyticsJump = (jump) => !isWeirdSegment(jump)

/**
 *
 * @param {Object} data         All this data are needed :
 *                                  session from useSession hook,
 *                                  jumps from useSessionJump hook,
 *                                  stridesStatistics from useSessionStridesStatistics hook,
 *                                  strides from useSessionStrides hook
 * @param {Number} startTime    Start time of stats in seconds
 * @param {Number} endTime      End time of stats in seconds
 */
const getFasterStride = (data, startTime = 0, endTime = null) => {
    if (!(data?.stridesStatistics?.length > 0) || !(data?.strides?.length > 0)) {
        return null
    }

    const analysisEndTime = endTime ?? data.session?.elapsed_time ?? startTime
    let maxIndex = null
    let maxValue = 0

    for (let strideIdx = 0; strideIdx < data.strides?.length; strideIdx++) {
        const stride = data.strides?.[strideIdx]
        const strideTime = toStrideTime(stride?.pos?.time)

        if (
            !Number.isFinite(strideTime) ||
            strideTime < startTime ||
            strideTime > analysisEndTime ||
            !isAnalyticsStride(stride)
        ) {
            continue
        }

        if (data.stridesStatistics[strideIdx]?.speed?.avg > maxValue) {
            maxIndex = strideIdx
            maxValue = data.stridesStatistics[strideIdx].speed.avg
        }
    }

    if (maxIndex === null) return null

    return { stride: data.strides[maxIndex], strideStatistics: data.stridesStatistics[maxIndex] }
}

/**
 *
 * @param {Object} data         All this data are needed :
 *                                  session from useSession hook,
 *                                  jumps from useSessionJump hook,
 *                                  stridesStatistics from useSessionStridesStatistics hook,
 *                                  strides from useSessionStrides hook
 * @param {Number} startTime    Start time of stats in seconds
 * @param {Number} endTime      End time of stats in seconds
 * @param {String} chunkType    Type of chunks 'length', 'duration'
 * @param {Number} chunkLength  Length of chunck
 *                              False: return all gaits
 * @returns
 */
const calculateStatisticsByChunk = (
    data,
    startTime = 0,
    endTime = null,
    chunkType = 'distance',
    chunkLength = 200,
    visibleStartTime = startTime
) => {
    let statistics = []
    const analysisEndTime = endTime ?? data.session?.elapsed_time ?? startTime

    switch (chunkType) {
        case 'duration':
            const duration = Math.max(0, analysisEndTime - startTime)
            const countChunks = Math.ceil(duration / chunkLength)
            for (let index = 0; index < countChunks; index++) {
                const chunkStartTime = startTime + index * chunkLength
                const targetEndTime = chunkStartTime + chunkLength
                const chunkEndTime = Math.min(analysisEndTime, targetEndTime)

                if (chunkEndTime <= chunkStartTime) {
                    continue
                }

                const chunkStatistics = calculateStatistics(data, chunkStartTime, chunkEndTime)

                chunkStatistics.isPartial = chunkEndTime < targetEndTime - 0.01
                chunkStatistics.targetEndTime = targetEndTime
                chunkStatistics.targetDuration = chunkLength

                if (chunkStatistics.endTime >= visibleStartTime && chunkStatistics.startTime <= analysisEndTime) {
                    statistics.push(chunkStatistics)
                }
            }
            break
        case 'distance':
            let chunkStartTime = startTime
            let chunkEndTime = analysisEndTime
            let jumps = [...(data.jumps ?? [])] //if not done it will modify the global object
            let strideIdx = 0
            while (strideIdx < data.strides?.length) {
                const stride = data.strides?.[strideIdx]
                const strideTime = toStrideTime(stride?.pos?.time)

                if (
                    !Number.isFinite(strideTime) ||
                    strideTime < startTime ||
                    strideTime > analysisEndTime
                ) {
                    strideIdx++
                    continue
                }

                let totalDistance = 0
                while (totalDistance < chunkLength && strideIdx < data.strides?.length) {
                    const nextStride = data.strides?.[strideIdx]
                    const nextStrideTime = toStrideTime(nextStride?.pos?.time)

                    if (!Number.isFinite(nextStrideTime)) {
                        strideIdx++
                        continue
                    }

                    if (isAnalyticsStride(nextStride)) {
                        totalDistance += data.stridesStatistics[strideIdx]?.distance?.sum ?? 0
                        chunkEndTime = nextStrideTime
                        chunkEndTime -= 0.01
                    }
                    //START JUMPS
                    for (let idx = 0; idx < jumps?.length; idx++) {
                        const jump = jumps[idx]
                        const jumpStartTime = toStrideTime(jump?.start?.time)
                        const jumpEndTime = toStrideTime(jump?.end?.time)

                        if (
                            !Number.isFinite(jumpStartTime) ||
                            !Number.isFinite(jumpEndTime) ||
                            jumpStartTime < chunkStartTime ||
                            jumpEndTime > chunkEndTime
                        ) {
                            continue
                        }
                        if (isAnalyticsJump(jump)) {
                            totalDistance += jump?.statistics?.distance?.sum ?? 0
                        }
                        jumps.splice(idx, 1) //to remove jump when added to distance
                        idx--
                    }
                    //END JUMPS
                    strideIdx++
                }

                const chunkStatistics = calculateStatistics(data, chunkStartTime, chunkEndTime)
                chunkStatistics.isPartial = totalDistance < chunkLength - 0.01
                chunkStatistics.targetDistance = chunkLength
                chunkStatistics.distanceCovered = totalDistance

                if (chunkStatistics.endTime >= visibleStartTime && chunkStatistics.startTime <= analysisEndTime) {
                    statistics.push(chunkStatistics)
                }
                chunkStartTime = chunkEndTime
            }
            break
    }
    return statistics
}

const getGaitBlocks = (data, gait = 'canter', startTime = 0, endTime = null) => {
    if (!(data?.stridesStatistics?.length > 0) || !(data?.strides?.length > 0)) {
        return []
    }

    const normalizedGait = `${gait ?? 'canter'}`.toLowerCase()
    if (!normalizedGait || normalizedGait === 'all') {
        return []
    }

    const maxTime = endTime ?? data.session?.elapsed_time ?? startTime
    const gaitBlocks = []
    let currentGaitBlock = null

    const closeGaitBlock = () => {
        if (!currentGaitBlock || currentGaitBlock.strideCount <= 0) {
            currentGaitBlock = null
            return
        }

        currentGaitBlock.endTime = currentGaitBlock.lastStrideTime ?? currentGaitBlock.startTime
        currentGaitBlock.averageCadence = currentGaitBlock.cadences.length
            ? average(currentGaitBlock.cadences)
            : 0

        gaitBlocks.push(currentGaitBlock)
        currentGaitBlock = null
    }

    for (let strideIdx = 0; strideIdx < data.strides.length; strideIdx++) {
        const stride = data.strides[strideIdx]
        const strideStatistics = data.stridesStatistics[strideIdx]

        const strideTime = toStrideTime(stride?.pos?.time)

        if (!Number.isFinite(strideTime) || !strideStatistics) {
            continue
        }

        if (strideTime < startTime || strideTime > maxTime) {
            continue
        }

        if (!isAnalyticsStride(stride)) {
            continue
        }

        if (stride.gait?.toLowerCase() !== normalizedGait) {
            closeGaitBlock()
            continue
        }

        if (!currentGaitBlock) {
            currentGaitBlock = {
                startTime: strideTime,
                endTime: strideTime,
                lastStrideTime: strideTime,
                strideCount: 0,
                cadences: [],
                averageCadence: 0,
            }
        }

        currentGaitBlock.strideCount += 1
        currentGaitBlock.lastStrideTime = strideTime

        const strideDuration = strideStatistics?.duration?.sum
        const cadence = Number.isFinite(strideDuration) && strideDuration > 0 ? 60 / strideDuration : null

        if (Number.isFinite(cadence)) {
            currentGaitBlock.cadences.push(cadence)
        }
    }

    closeGaitBlock()

    return gaitBlocks.map(({ lastStrideTime, ...block }) => block)
}

const getClosestGaitBlock = (gaitBlocks = [], referenceTime = undefined) => {
    if (!gaitBlocks.length) {
        return null
    }

    if (!Number.isFinite(referenceTime)) {
        return gaitBlocks[0]
    }

    const containingBlock = gaitBlocks.find(
        (block) => referenceTime >= block.startTime - 0.01 && referenceTime <= block.endTime + 0.01
    )

    if (containingBlock) {
        return containingBlock
    }

    return gaitBlocks.reduce((bestBlock, currentBlock) => {
        if (!bestBlock) {
            return currentBlock
        }

        const bestDistance =
            referenceTime < bestBlock.startTime
                ? bestBlock.startTime - referenceTime
                : referenceTime > bestBlock.endTime
                  ? referenceTime - bestBlock.endTime
                  : 0

        const currentDistance =
            referenceTime < currentBlock.startTime
                ? currentBlock.startTime - referenceTime
                : referenceTime > currentBlock.endTime
                  ? referenceTime - currentBlock.endTime
                  : 0

        return currentDistance < bestDistance ? currentBlock : bestBlock
    }, null)
}

const detectFurlongStartTime = (data, startTime = 0, endTime = null, gait = 'canter', referenceTime = undefined) => {
    const normalizedGait = `${gait ?? 'canter'}`.toLowerCase()
    const targetGait = normalizedGait === 'all' ? 'canter' : normalizedGait
    const gaitBlocks = getGaitBlocks(data, targetGait, startTime, endTime)

    if (!gaitBlocks.length) {
        return startTime
    }

    if (Number.isFinite(referenceTime)) {
        return getClosestGaitBlock(gaitBlocks, referenceTime)?.startTime ?? gaitBlocks[0]?.startTime ?? startTime
    }

    const gaitCadences = gaitBlocks.flatMap((block) => block.cadences)

    if (!gaitCadences.length) {
        return gaitBlocks[0]?.startTime ?? startTime
    }

    const averageGaitCadence = average(gaitCadences)
    const maxGaitCadence = gaitCadences.reduce((currentMax, cadence) => Math.max(currentMax, cadence), 0)
    const minimumCadenceByGait = {
        canter: 55,
        trot: 45,
        walk: 30,
    }
    const cadenceThreshold = Math.max(
        minimumCadenceByGait[targetGait] ?? 40,
        averageGaitCadence * 0.9,
        maxGaitCadence * 0.75
    )

    const preferredGaitBlock =
        gaitBlocks.find((block) => block.strideCount >= 4 && block.averageCadence >= cadenceThreshold) ??
        [...gaitBlocks].sort((blockA, blockB) => blockB.averageCadence - blockA.averageCadence)[0]

    return preferredGaitBlock?.startTime ?? gaitBlocks[0]?.startTime ?? startTime
}

const detectSuggestedFurlongStart = (data, startTime = 0, endTime = null) => {
    const canterBlocks = getGaitBlocks(data, 'canter', startTime, endTime)

    if (canterBlocks.length) {
        const sustainedCanterBlock =
            canterBlocks.find((block) => (block.endTime ?? block.startTime ?? 0) - (block.startTime ?? 0) >= MIN_SUSTAINED_CANTER_DURATION) ??
            canterBlocks[0]

        return {
            gait: 'canter',
            startTime: sustainedCanterBlock?.startTime ?? startTime,
        }
    }

    return {
        gait: undefined,
        startTime,
    }
}

const getGaitBlockRange = (data, gait = 'all', startTime = 0, endTime = null) => {
    if (!(data?.strides?.length > 0) || !gait || gait === 'all') {
        return null
    }

    const gaitBlocks = getGaitBlocks(data, gait, 0, endTime)
    return getClosestGaitBlock(gaitBlocks, startTime)
}

const calculateFurlongStatistics = (
    data,
    furlongStartTime = 0,
    startTime = 0,
    endTime = null,
    furlongLength = FURLONG_IN_METERS
) => {
    if (!(data?.stridesStatistics?.length > 0) || !(data?.strides?.length > 0)) {
        return []
    }

    const analysisStartTime = Math.max(0, furlongStartTime ?? 0)
    const analysisEndTime = endTime ?? data.session?.elapsed_time ?? analysisStartTime

    if (analysisEndTime <= analysisStartTime) {
        return []
    }

    const statistics = []
    let chunkIndex = 1
    let chunkDistance = 0
    let chunkStartTime = null
    let lastStrideTime = null

    const appendChunk = (chunkEndTime, isPartial = false) => {
        if (chunkStartTime === null || chunkEndTime === null || chunkEndTime < chunkStartTime) {
            return
        }

        const chunkStatistics = calculateStatistics(data, chunkStartTime, chunkEndTime)
        const isVisible = chunkEndTime >= startTime && chunkStartTime <= analysisEndTime

        if (isVisible) {
            statistics.push({
                ...chunkStatistics,
                furlongIndex: chunkIndex,
                furlongLabel: `F${chunkIndex}`,
                furlongDistanceTarget: furlongLength,
                furlongDistanceCovered: chunkStatistics.distance,
                isPartial,
            })
        }

        chunkIndex += 1
    }

    for (let strideIdx = 0; strideIdx < data.strides.length; strideIdx++) {
        const stride = data.strides[strideIdx]
        const strideStatistics = data.stridesStatistics[strideIdx]

        const strideTime = toStrideTime(stride?.pos?.time)

        if (!Number.isFinite(strideTime) || !strideStatistics) {
            continue
        }

        if (strideTime < analysisStartTime) {
            continue
        }

        if (strideTime > analysisEndTime) {
            // Strides are expected to be sorted by time, so we can stop scanning here.
            break
        }

        if (!isAnalyticsStride(stride)) {
            continue
        }

        if (chunkStartTime === null) {
            chunkStartTime = strideTime
        }

        lastStrideTime = strideTime
        chunkDistance += strideStatistics?.distance?.sum ?? 0

        if (chunkDistance >= furlongLength) {
            appendChunk(lastStrideTime, false)
            chunkDistance = 0
            chunkStartTime = null
            lastStrideTime = null
        }
    }

    if (chunkStartTime !== null && lastStrideTime !== null) {
        appendChunk(lastStrideTime, true)
    }

    return statistics
}

/**
 *
 * @param {Object} data         All this data are needed :
 *                                  session from useSession hook,
 *                                  jumps from useSessionJump hook,
 *                                  stridesStatistics from useSessionStridesStatistics hook,
 *                                  strides from useSessionStrides hook
 * @param {Number} startTime    Start time of stats in seconds
 * @param {Number} endTime      End time of stats in seconds
 *                              False: return all gaits
 * @returns
 */
const calculateStatistics = (data, startTime = 0, endTime = null) => {
    // console.time('live-stats')

    const totalWeight = getTotalWeight(data.session?.horse?.weight, data.session?.rider)
    const analysisEndTime = endTime ?? data.session?.elapsed_time ?? startTime
    const normalizeForceValue = (value) => {
        const numeric = Number(value)
        return Number.isFinite(numeric) ? numeric : null
    }

    const createInitialValues = () => ({
        stride_left_hand_count: 0,
        stride_straight_hand_count: 0,
        stride_right_hand_count: 0,
        stride_gait_count: 0,
        stride_gait_countRegular: 0,
        stride_speed_average: 0,
        stride_speed_max: 0,
        stride_speed_min: Infinity,
        stride_yaw_average: 0,
        stride_roll_average: 0,
        stride_pitch_average: 0,
        stride_energy_average: 0,
        stride_distance_average: 0,
        stride_distance_max: 0,
        stride_distance_min: Infinity,
        stride_bpm_count: 0,
        stride_bpm_average: 0,
        stride_bpm_max: 0,
        stride_bpm_min: Infinity,
        stride_left_force_count: 0,
        stride_left_force_average: 0,
        stride_left_force_max: null,
        stride_right_force_count: 0,
        stride_right_force_average: 0,
        stride_right_force_max: null,
        stride_duration_average: 0,
        stride_duration_max: 0,
        stride_duration_min: Infinity,
        stride_height_average: 0,
        stride_height_max: 0,
        stride_height_min: Infinity,
        stride_classification: [], //here it will de an object returned {regularity,values,...}
        stride_power_average: 0,
        strides_duration: 0,
        strides_distance: 0,
        strides_energy: 0,
        jump_count: 0,
        gait_duration: 0,
        gait_distance: 0,
        gait_energy: 0,
        gait_transition_count: 0,
        stride_cadence_average: 0,
    })
    const initialValues = createInitialValues()

    let gaits = {
        canter: {
            ...createInitialValues(),
            cadence_coefficient: 1,
        },
        trot: {
            ...createInitialValues(),
            cadence_coefficient: 0.5,
        },
        walk: {
            ...createInitialValues(),
            cadence_coefficient: 0.5,
        },
        standing: {
            ...createInitialValues(),
            cadence_coefficient: 0,
        },
    }

    let haveBpm = false
    let haveReinTension = false
    let energy = 0
    let distance = 0
    let duration = 0
    let analyticsJumpDuration = 0
    let analyticsJumpDistance = 0
    let jump_duration_average = 0
    let jump_distance_average = 0
    let jump_height_average = 0
    let jump_z_power_average = 0
    let jump_speed_average = 0
    let jump_energy_average = 0
    let jump_pitch_average = 0
    let jump_roll_average = 0
    let jump_yaw_average = 0
    let jump_pitch_max = 0
    let jump_peak_height = 0
    let jump_classification = [] //here it will de an object returned {regularity,values,...}
    let jumpCount = 0
    let analyticsJumpCount = 0
    let strideCount = 0
    let stride_before_distance_average = 0
    let stride_before_height_average = 0
    let stride_after_distance_average = 0
    let stride_after_height_average = 0
    let strideBeforeDistanceCount = 0
    let strideBeforeHeightCount = 0
    let strideAfterDistanceCount = 0
    let strideAfterHeightCount = 0
    let firstHalfSection = { pitch: [] }
    let secondHalfSection = { pitch: [] }
    const halfTime = (startTime + analysisEndTime) / 2

    for (let idx = 0; idx < data.jumps?.length; idx++) {
        const jump = data.jumps[idx]
        const jumpStartTime = toStrideTime(jump?.start?.time)
        const jumpEndTime = toStrideTime(jump?.end?.time)

        if (
            !Number.isFinite(jumpStartTime) ||
            !Number.isFinite(jumpEndTime) ||
            jumpStartTime < startTime ||
            jumpEndTime > analysisEndTime
        ) {
            continue
        }

        const jumpGait = gaits[`${jump?.gait ?? ''}`.toLowerCase()] ? `${jump?.gait ?? ''}`.toLowerCase() : 'standing'
        const jumpDuration = jump?.statistics?.duration?.sum || 0

        jumpCount++
        gaits[jumpGait].jump_count += 1
        gaits[jumpGait].gait_duration += jumpDuration
        duration += jumpDuration

        if (!isAnalyticsJump(jump)) {
            continue
        }

        const jumpDistance = jump?.statistics?.distance?.sum || 0
        const jumpEnergy = jump?.statistics?.energy?.sum || 0

        gaits[jumpGait].gait_distance += jumpDistance
        gaits[jumpGait].gait_energy += jumpEnergy

        distance += jumpDistance
        analyticsJumpDistance += jumpDistance
        jump_height_average += jump?.statistics?.height?.max || 0
        jump_z_power_average += jump?.statistics?.power?.max || 0
        jump_speed_average += jump?.statistics?.speed?.avg || 0
        jump_energy_average += jumpEnergy
        energy += jumpEnergy
        analyticsJumpDuration += jumpDuration
        jump_pitch_average += jump?.statistics?.pitch?.max || 0
        jump_roll_average += jump?.statistics?.roll?.avg || 0
        jump_yaw_average += jump?.statistics?.yaw?.avg || 0
        if (jump_pitch_max < jump?.statistics?.pitch?.max) {
            jump_pitch_max = jump?.statistics?.pitch?.max || 0
        }
        if (jump_peak_height < jump?.statistics?.peakheight?.max) {
            jump_peak_height = jump?.statistics?.peakheight?.max || 0
        }
        const jumpArea = Number(jump?.area)
        if (Number.isFinite(jumpArea)) {
            jump_classification.push({ value: jumpArea, index: idx })
        }

        const jumpLastStrideIndex = jump?.strides?.last
        const strideBefore = Number.isInteger(jumpLastStrideIndex) ? data.strides?.[jumpLastStrideIndex] : undefined
        const strideAfter = Number.isInteger(jumpLastStrideIndex) ? data.strides?.[jumpLastStrideIndex + 1] : undefined
        const strideStatisticsBefore = Number.isInteger(jumpLastStrideIndex)
            ? data.stridesStatistics?.[jumpLastStrideIndex]
            : undefined
        const strideStatisticsAfter = Number.isInteger(jumpLastStrideIndex)
            ? data.stridesStatistics?.[jumpLastStrideIndex + 1]
            : undefined

        if (isAnalyticsStride(strideBefore) && strideStatisticsBefore) {
            stride_before_distance_average += strideStatisticsBefore?.distance?.sum || 0
            stride_before_height_average += strideStatisticsBefore?.height?.max || 0
            strideBeforeDistanceCount++
            strideBeforeHeightCount++
        }

        if (isAnalyticsStride(strideAfter) && strideStatisticsAfter) {
            stride_after_distance_average += strideStatisticsAfter?.distance?.sum || 0
            stride_after_height_average += strideStatisticsAfter?.height?.max || 0
            strideAfterDistanceCount++
            strideAfterHeightCount++
        }

        analyticsJumpCount++
    }

    if (analyticsJumpCount > 0) {
        jump_duration_average = analyticsJumpDuration / analyticsJumpCount
        jump_distance_average = analyticsJumpDistance / analyticsJumpCount
        jump_height_average = jump_height_average / analyticsJumpCount
        jump_z_power_average = jump_z_power_average / analyticsJumpCount
        jump_speed_average = jump_speed_average / analyticsJumpCount
        jump_energy_average = (jump_energy_average / analyticsJumpCount) * totalWeight
        jump_pitch_average = jump_pitch_average / analyticsJumpCount
        jump_roll_average = jump_roll_average / analyticsJumpCount
        jump_yaw_average = jump_yaw_average / analyticsJumpCount

        jump_classification = regularityAndClassification(jump_classification) //transform the array into an object with regularity etc.

        stride_before_distance_average =
            strideBeforeDistanceCount > 0 ? stride_before_distance_average / strideBeforeDistanceCount : 0
        stride_before_height_average =
            strideBeforeHeightCount > 0 ? stride_before_height_average / strideBeforeHeightCount : 0
        stride_after_distance_average =
            strideAfterDistanceCount > 0 ? stride_after_distance_average / strideAfterDistanceCount : 0
        stride_after_height_average =
            strideAfterHeightCount > 0 ? stride_after_height_average / strideAfterHeightCount : 0
    }

    let stride_gait = null

    const stridesIndexBeforeOrAfterJump = new Set(
        (data.jumps ?? [])
            .map((jump) => (Number.isInteger(jump?.strides?.last) ? [jump.strides.last, jump.strides.last + 1] : []))
            .flat()
    )
    const allStrideClassification = []

    let previousGait = null

    for (let strideIdx = 0; strideIdx < data.strides?.length; strideIdx++) {
        const currentStride = data.strides[strideIdx]
        const strideTime = toStrideTime(currentStride?.pos?.time)

        if (!Number.isFinite(strideTime)) {
            continue
        }

        if (strideTime < startTime || strideTime > analysisEndTime) {
            continue
        }

        const currentStrideStatistics = data.stridesStatistics?.[strideIdx]
        // console.log('currentStrideStatistics', currentStrideStatistics)

        if (!currentStrideStatistics) {
            continue
        }

        if (currentStrideStatistics['id'] !== currentStride['id']) {
            continue
        }

        const strideDuration = currentStrideStatistics.duration.sum || 0
        const strideDistance = currentStrideStatistics.distance.sum || 0
        const strideEnergy = currentStrideStatistics.energy.sum || 0
        const currentGait = gaits[`${currentStride?.gait ?? ''}`.toLowerCase()]
            ? `${currentStride?.gait ?? ''}`.toLowerCase()
            : 'standing'

        duration += strideDuration // total duration stays raw to preserve the session timeline
        strideCount++
        gaits[currentGait].gait_duration += strideDuration

        if (!isAnalyticsStride(currentStride)) {
            continue
        }

        distance += strideDistance // total distance is aggregated in valid-only mode
        energy += strideEnergy

        if (currentGait !== previousGait) {
            gaits[currentGait].gait_transition_count += 1
            previousGait = currentGait
        }

        switch (currentStrideStatistics['hand']) {
            case 'l':
                gaits[currentGait].stride_left_hand_count += 1
                break
            case 's':
                gaits[currentGait].stride_straight_hand_count += 1
                break
            case 'r':
                gaits[currentGait].stride_right_hand_count += 1
                break
        }

        //#### sort data by hand
        // if (strideIdx > 1 && strideIdx <= data.strides.length) {
        //     const { pos: prevPos } = data.strides[strideIdx - 2]
        //     const { pos: curPos } = data.strides[strideIdx - 1]
        //     const { pos: nextPos } = data.strides[strideIdx]

        //     const ux = Math.round(parseFloat(curPos.x - prevPos.x) * 100) / 100
        //     const uy = Math.round(parseFloat(curPos.y - prevPos.y) * 100) / 100
        //     const u2x = Math.round(parseFloat(nextPos.x - curPos.x) * 100) / 100
        //     const u2y = Math.round(parseFloat(nextPos.y - curPos.y) * 100) / 100
        //     if (strideIdx < 40) {
        //         const teta1 =
        //             Math.round(parseFloat(Math.atan(Math.abs(uy) / Math.abs(ux)) * (180 / Math.PI)) * 100) / 100
        //         // const teta1 = Math.round(parseFloat(Math.atan(uy / ux) * (180 / Math.PI)) * 100) / 100
        //         // const orientation1 = 180 - teta1
        //         const teta2 =
        //             Math.round(parseFloat(Math.atan(Math.abs(u2y) / Math.abs(u2x)) * (180 / Math.PI)) * 100) / 100
        //         // const teta2 = Math.round(parseFloat(Math.atan(u2y / u2x) * (180 / Math.PI)) * 100) / 100
        //         // const orientation2 = 180 - teta2
        //         const isRight = teta2 > teta1
        //         const isStraight = Math.abs(teta2 - teta1) < 12
        //         const direction = isStraight ? 'straight' : isRight ? 'right' : 'left'
        //         // console.log(`${strideIdx} ${teta1} ${teta2} ${direction}`)
        //     }
        // }
        //####

        // # STATISTICS HALF SECTION
        if (strideTime < halfTime) {
            firstHalfSection['pitch'].push(currentStrideStatistics.pitch.avg)
        } else {
            secondHalfSection['pitch'].push(currentStrideStatistics.pitch.avg)
        }
        //####

        gaits[currentGait].stride_gait_count += 1
        gaits[currentGait].strides_duration += strideDuration
        gaits[currentGait].stride_cadence_average += 1
        gaits[currentGait].strides_distance += strideDistance // distance by gait in valid-only mode
        gaits[currentGait].strides_energy += strideEnergy // energy by gait in valid-only mode
        gaits[currentGait].gait_distance += strideDistance
        gaits[currentGait].gait_energy += strideEnergy

        // see https://github.com/Alogo-Analysis/issues-private/issues/6
        if (gaits[currentGait].stride_speed_max < currentStrideStatistics.speed.avg) {
            gaits[currentGait].stride_speed_max = currentStrideStatistics.speed.avg
        }

        // ignore the strides before and after the jumps and the strides with two differents gaits
        if (stridesIndexBeforeOrAfterJump.has(strideIdx) || currentStride.changeover === true) {
            continue
        }

        // todo 20210928 currently not implemented because we are not sure yet if we have to keep those strides into the global statistics
        // if (currentStride.indoor === 1) {
        //     // console.log('skip indoor stride')
        //     continue
        // }

        gaits[currentGait].stride_gait_countRegular += 1
        gaits[currentGait].stride_speed_average += currentStrideStatistics.speed.avg

        if (gaits[currentGait].stride_speed_min > currentStrideStatistics.speed.avg) {
            gaits[currentGait].stride_speed_min = currentStrideStatistics.speed.avg
        }
        gaits[currentGait].stride_yaw_average += currentStrideStatistics.yaw.avg
        gaits[currentGait].stride_roll_average += currentStrideStatistics.roll.avg
        gaits[currentGait].stride_pitch_average += currentStrideStatistics.pitch.avg
        gaits[currentGait].stride_energy_average += currentStrideStatistics.energy.sum
        gaits[currentGait].stride_distance_average += currentStrideStatistics.distance.sum
        if (gaits[currentGait].stride_distance_max < currentStrideStatistics.distance.sum) {
            gaits[currentGait].stride_distance_max = currentStrideStatistics.distance.sum
        }
        if (gaits[currentGait].stride_distance_min > currentStrideStatistics.distance.sum) {
            gaits[currentGait].stride_distance_min = currentStrideStatistics.distance.sum
        }

        const strideArea = Number(currentStride.area)
        if (Number.isFinite(strideArea)) {
            gaits[currentGait].stride_classification.push({ value: strideArea, index: strideIdx })
            allStrideClassification.push({ value: strideArea, index: strideIdx })
        }

        if (currentStride.bpm) {
            haveBpm = true
            gaits[currentGait].stride_bpm_count += 1
            gaits[currentGait].stride_bpm_average += currentStride.bpm
            if (gaits[currentGait].stride_bpm_max < currentStride.bpm) {
                gaits[currentGait].stride_bpm_max = currentStride.bpm
            }
            if (gaits[currentGait].stride_bpm_min > currentStride.bpm) {
                gaits[currentGait].stride_bpm_min = currentStride.bpm
            }
        }

        const leftForceAvg = normalizeForceValue(currentStrideStatistics?.left_force_n?.avg)
        const leftForceMax = normalizeForceValue(currentStrideStatistics?.left_force_n?.max)
        if (leftForceAvg !== null) {
            haveReinTension = true
            gaits[currentGait].stride_left_force_count += 1
            gaits[currentGait].stride_left_force_average += leftForceAvg
        }
        if (leftForceMax !== null) {
            haveReinTension = true
            if (
                gaits[currentGait].stride_left_force_max === null ||
                gaits[currentGait].stride_left_force_max < leftForceMax
            ) {
                gaits[currentGait].stride_left_force_max = leftForceMax
            }
        }

        const rightForceAvg = normalizeForceValue(currentStrideStatistics?.right_force_n?.avg)
        const rightForceMax = normalizeForceValue(currentStrideStatistics?.right_force_n?.max)
        if (rightForceAvg !== null) {
            haveReinTension = true
            gaits[currentGait].stride_right_force_count += 1
            gaits[currentGait].stride_right_force_average += rightForceAvg
        }
        if (rightForceMax !== null) {
            haveReinTension = true
            if (
                gaits[currentGait].stride_right_force_max === null ||
                gaits[currentGait].stride_right_force_max < rightForceMax
            ) {
                gaits[currentGait].stride_right_force_max = rightForceMax
            }
        }

        gaits[currentGait].stride_duration_average += currentStrideStatistics.duration.sum
        if (gaits[currentGait].stride_duration_max < currentStrideStatistics.duration.sum) {
            gaits[currentGait].stride_duration_max = currentStrideStatistics.duration.sum
        }
        if (gaits[currentGait].stride_duration_min > currentStrideStatistics.duration.sum) {
            gaits[currentGait].stride_duration_min = currentStrideStatistics.duration.sum
        }
        gaits[currentGait].stride_height_average += currentStrideStatistics.height.max
        if (gaits[currentGait].stride_height_max < currentStrideStatistics.height.max) {
            gaits[currentGait].stride_height_max = currentStrideStatistics.height.max
        }
        if (gaits[currentGait].stride_height_min > currentStrideStatistics.height.max) {
            gaits[currentGait].stride_height_min = currentStrideStatistics.height.max
        }
        gaits[currentGait].stride_power_average += currentStrideStatistics.power.max
    } //end of for on strides

    //calculate the average for each half statistics
    firstHalfSection['pitch'] = firstHalfSection['pitch'].length ? average(firstHalfSection['pitch']) : 0
    secondHalfSection['pitch'] = secondHalfSection['pitch'].length ? average(secondHalfSection['pitch']) : 0

    // Define higher gait
    if (gaits['canter'].stride_gait_countRegular > 0) {
        stride_gait = 'canter'
    } else if (gaits['trot'].stride_gait_countRegular > 0) {
        stride_gait = 'trot'
    } else if (gaits['walk'].stride_gait_countRegular > 0) {
        stride_gait = 'walk'
    } else {
        stride_gait = 'standing'
    }

    if (jump_height_average > 0) {
        jump_height_average = jump_height_average + (data.session?.horse?.sensor_height ?? 0) / 100
    }

    let all = createInitialValues()
    const keys = Object.keys(initialValues)
    let averageKeys = []
    Object.keys(gaits).forEach(function (gait) {
        gaits[gait].strides_energy *= totalWeight // Watts

        if (gaits[gait].stride_gait_countRegular > 0) {
            const strideDurationInMinutes = gaits[gait].strides_duration / 60

            gaits[gait].stride_cadence_average =
                strideDurationInMinutes > 0
                    ? (gaits[gait].stride_cadence_average / strideDurationInMinutes) * gaits[gait]['cadence_coefficient']
                    : 0

            gaits[gait].stride_speed_average = gaits[gait].stride_speed_average / gaits[gait].stride_gait_countRegular
            gaits[gait].stride_yaw_average = gaits[gait].stride_yaw_average / gaits[gait].stride_gait_countRegular
            gaits[gait].stride_roll_average = gaits[gait].stride_roll_average / gaits[gait].stride_gait_countRegular
            gaits[gait].stride_pitch_average = gaits[gait].stride_pitch_average / gaits[gait].stride_gait_countRegular
            gaits[gait].stride_energy_average =
                (gaits[gait].stride_energy_average / gaits[gait].stride_gait_countRegular) * totalWeight
            gaits[gait].stride_duration_average =
                gaits[gait].stride_duration_average / gaits[gait].stride_gait_countRegular
            gaits[gait].stride_bpm_average =
                gaits[gait].stride_bpm_count > 0 ? gaits[gait].stride_bpm_average / gaits[gait].stride_bpm_count : 0
            gaits[gait].stride_left_force_average =
                gaits[gait].stride_left_force_count > 0
                    ? gaits[gait].stride_left_force_average / gaits[gait].stride_left_force_count
                    : 0
            gaits[gait].stride_right_force_average =
                gaits[gait].stride_right_force_count > 0
                    ? gaits[gait].stride_right_force_average / gaits[gait].stride_right_force_count
                    : 0
            gaits[gait].stride_distance_average =
                gaits[gait].stride_distance_average / gaits[gait].stride_gait_countRegular
            gaits[gait].stride_height_average = gaits[gait].stride_height_average / gaits[gait].stride_gait_countRegular
            gaits[gait].stride_power_average = gaits[gait].stride_power_average / gaits[gait].stride_gait_countRegular
            //Calculate the standard deviation / transform array to a value

            gaits[gait].stride_classification = regularityAndClassification(gaits[gait].stride_classification, 100) //transform the array into an object with regularity etc.

            //ALL GAITS
            for (let index = 0; index < keys.length; index++) {
                const key = keys[index]
                const keySplitted = key.split('_')
                const kindOfKey = keySplitted[keySplitted.length - 1]

                switch (kindOfKey) {
                    case 'count':
                    case 'countRegular':
                        all[key] += gaits[gait][key]
                        break
                    case 'average':
                        all[key] += gaits[gait][key] * gaits[gait]['stride_gait_countRegular']
                        averageKeys.push(key)
                        break
                    case 'max':
                        if (
                            Number.isFinite(gaits[gait][key]) &&
                            (!Number.isFinite(all[key]) || all[key] < gaits[gait][key])
                        ) {
                            all[key] = gaits[gait][key]
                        }
                        break
                    case 'min':
                        if (all[key] > gaits[gait][key]) {
                            all[key] = gaits[gait][key]
                        }
                        break
                    case 'stddev':
                        //ignore
                        break
                    case 'regularity':
                        all[key] += gaits[gait][key] * gaits[gait]['stride_gait_countRegular']
                        averageKeys.push(key)
                        break
                    case 'duration':
                        all[key] += gaits[gait][key]
                        break
                    case 'distance':
                        all[key] += gaits[gait][key]
                        break
                    case 'energy':
                        all[key] += gaits[gait][key]
                        break
                    case 'classification':
                        break
                    default:
                        break
                }
            }
        }

        sanitizeMinStatistics(gaits[gait])
        gaits[gait].haveLeftReinTension =
            gaits[gait].stride_left_force_count > 0 || Number.isFinite(gaits[gait].stride_left_force_max)
        gaits[gait].haveRightReinTension =
            gaits[gait].stride_right_force_count > 0 || Number.isFinite(gaits[gait].stride_right_force_max)
        gaits[gait].haveReinTension = gaits[gait].haveLeftReinTension || gaits[gait].haveRightReinTension
    })

    all.stride_classification = regularityAndClassification(allStrideClassification, 100)

    //average for the 'all' gait keys
    const uniqueAverageKeys = [...new Set(averageKeys)]
    const allRegularStrideCount = all['stride_gait_countRegular'] || 0
    for (let index = 0; index < uniqueAverageKeys.length; index++) {
        const key = uniqueAverageKeys[index]
        all[key] = allRegularStrideCount > 0 ? Math.round((all[key] / allRegularStrideCount) * 10000) / 10000 : 0
    }

    //bpm is different and can be empty in some gait
    if (haveBpm) {
        let count = 0
        let avg = 0
        Object.keys(gaits).forEach(function (gait) {
            count += gaits[gait]['stride_bpm_count']
            avg += gaits[gait]['stride_bpm_average'] * gaits[gait]['stride_bpm_count']
        })
        all['stride_bpm_average'] = count > 0 ? Math.round(avg / count) : 0
    }

    if (haveReinTension) {
        let leftCount = 0
        let leftAvg = 0
        let rightCount = 0
        let rightAvg = 0

        Object.keys(gaits).forEach(function (gait) {
            leftCount += gaits[gait]['stride_left_force_count']
            leftAvg += gaits[gait]['stride_left_force_average'] * gaits[gait]['stride_left_force_count']

            rightCount += gaits[gait]['stride_right_force_count']
            rightAvg += gaits[gait]['stride_right_force_average'] * gaits[gait]['stride_right_force_count']
        })

        all['stride_left_force_average'] = leftCount > 0 ? Math.round((leftAvg / leftCount) * 10000) / 10000 : 0
        all['stride_right_force_average'] = rightCount > 0 ? Math.round((rightAvg / rightCount) * 10000) / 10000 : 0
        haveReinTension =
            leftCount > 0 ||
            rightCount > 0 ||
            Number.isFinite(all['stride_left_force_max']) ||
            Number.isFinite(all['stride_right_force_max'])
    }

    all.haveLeftReinTension = all.stride_left_force_count > 0 || Number.isFinite(all.stride_left_force_max)
    all.haveRightReinTension = all.stride_right_force_count > 0 || Number.isFinite(all.stride_right_force_max)
    all.haveReinTension = all.haveLeftReinTension || all.haveRightReinTension
    haveReinTension = all.haveReinTension

    sanitizeMinStatistics(all)

    energy = energy * totalWeight // Watts

    // console.timeEnd('live-stats')

    return {
        haveBpm,
        haveReinTension,
        haveLeftReinTension: all.haveLeftReinTension,
        haveRightReinTension: all.haveRightReinTension,
        startTime,
        endTime: analysisEndTime,
        stride_gait: stride_gait,
        elapsed_time: secondsToHMS(analysisEndTime - startTime, 3),
        energy: energy, // Watts
        distance: distance,
        duration: duration,
        // duration: secondsToHMS(duration, 3),//what was done before but not the right place to do the convertion
        jump_count: jumpCount,
        stride_count: strideCount,
        jump_height_average: jump_height_average,
        jump_z_power_average: jump_z_power_average,

        jump_speed_average: jump_speed_average,
        jump_energy_average: jump_energy_average,
        jump_pitch_average: jump_pitch_average,
        jump_roll_average: jump_roll_average,
        jump_yaw_average: jump_yaw_average,
        jump_pitch_max: jump_pitch_max,
        jump_peak_height: jump_peak_height,
        jump_duration_average: jump_duration_average,
        jump_distance_average: jump_distance_average,
        jump_classification: jump_classification,

        stride_before_distance_average: stride_before_distance_average,
        stride_before_height_average: stride_before_height_average,
        stride_after_distance_average: stride_after_distance_average,
        stride_after_height_average: stride_after_height_average,

        //half statistics
        firstHalfSectionPitch: firstHalfSection['pitch'],
        secondHalfSectionPitch: secondHalfSection['pitch'],

        gaits: { ...gaits, all },
    }
}

const colorThresholds = {
    duration: 1, // seconds
    strideDistance: 5, // meters
    jumpDistance: 8, // meters
    strideDuration: 0.8, // seconds
    jumpDuration: 1.2, // seconds
    jumpHeight: 0.8, // meters
    strideHeight: 0.2, // meters
    jumpPower: 8, // G
    strikePower: 4, // G
    speed: 500, // meter/min
    angle: 10, // degrees
    pitchAngle: 45, // degrees
    cadence: 100, // strides/min
    percent: 100, //
    intensity: 50, //
    energy: 5000, // Watts
    energyMaxPerMinute: 150, //Watts
    bpm: 200, //bpm
    speed_stddev: 80, //std
    distance_stddev: 0.6, //std
    height_stddev: 0.08,
    roll_stddev: 4,
    pitch_stddev: 4,
    yaw_stddev: 4,
    power_stddev: 0.5, //std
    intensity_stddev: 3, //std
    energy_stddev: 220, //std
}

const equilibriumTresholds = {
    pitch: 5,
    yaw: 5,
    roll: 5,
}

const healthEventTresholds = {
    blacksmith: {
        warning: {
            value: 6,
            unit: 'week',
        },
        danger: {
            value: 8,
            unit: 'week',
        },
    },
    vaccin: {
        warning: {
            value: 11,
            unit: 'month',
        },
        danger: {
            value: 12,
            unit: 'month',
        },
    },
    dentist: {
        warning: {
            value: 11,
            unit: 'month',
        },
        danger: {
            value: 12,
            unit: 'month',
        },
    },
}

const standardDeviation = (arr, mean) => {
    if (!arr.length) {
        return 0
    }

    return (
        Math.round(
            Math.sqrt(
                arr.reduce((acc, val) => acc.concat((val - mean) ** 2), []).reduce((acc, val) => acc + val, 0) /
                    arr.length
            ) * 1000
        ) / 1000
    )
}

const sum = (values) =>
    values.reduce(function (a, b) {
        const numericValue = b && typeof b === 'object' && !Array.isArray(b) ? Number(b.value) : Number(b)
        return a + (Number.isFinite(numericValue) ? numericValue : 0)
    }, 0)

const sanitizeMinStatistics = (statistics) => {
    const minKeys = [
        'stride_speed_min',
        'stride_distance_min',
        'stride_bpm_min',
        'stride_duration_min',
        'stride_height_min',
    ]

    for (const key of minKeys) {
        if (!Number.isFinite(statistics?.[key])) {
            statistics[key] = 0
        }
    }

    return statistics
}

const regularityAndClassification = (values, outlayerPercent = 30) => {
    const nbValues = values.length
    if (nbValues === 0) {
        return {
            regularCount: 0,
            unsualCount: 0,
            regularity: 0,
            classification: [],
        }
    }

    if (nbValues < 3) {
        outlayerPercent = 99
    }
    const average = sum(values) / nbValues
    let classification = []
    let regularValues = [] //regular values to calculate the regularity scoring
    let regularCount = 0
    let unsualCount = 0
    for (let index = 0; index < nbValues; index++) {
        const element = values[index]
        const value = element && typeof element === 'object' && !Array.isArray(element) ? Number(element.value) : Number(element)
        const sourceIndex =
            element && typeof element === 'object' && !Array.isArray(element) && Number.isInteger(element.index)
                ? element.index
                : index

        if (!Number.isFinite(value)) {
            continue
        }

        let unusual = false
        let regular = false
        const abs = Math.abs(average - value)
        const percent = average === 0 ? (abs === 0 ? 0 : 100) : (abs * 100) / average
        // console.log(percent + '%' + ' ' + abs + ' ' + element + ' ' + average)
        if (percent > outlayerPercent) {
            unusual = true
            unsualCount++
        } else {
            // }
            // if (percent <= regularPercent) {

            //jumps to take for the regularity
            regularValues.push(value)
            regular = true
            regularCount++
        }
        classification.push({ index: sourceIndex, unusual, regular, percent })
    }

    // Calculate the regularity scoring for "good" and "similar" values
    let regularity = 0
    let countRegular = regularValues.length
    if (countRegular > 0) {
        let averageRegular = sum(regularValues) / countRegular

        let sumAbsDiff = 0
        for (let index = 0; index < countRegular; index++) {
            const element = regularValues[index]

            sumAbsDiff += Math.abs(averageRegular - element)
        }
        if (averageRegular === 0) {
            regularity = Math.round(100 * (countRegular / nbValues))
        } else {
            regularity = ((averageRegular - sumAbsDiff / countRegular) * 100) / averageRegular
            regularity = Math.round(regularity * (countRegular / nbValues))
        }
        regularity = regularity < 0 ? 0 : regularity //fix to prevent negative regularity
        // averageRegular = $this->jumpsArea(regularValues);
        // sumAbsDiff = 0;
        // foreach ($jumps as $jump) {
        //     sumAbsDiff += abs(averageRegular - $jump['area']);
        // }
        // countRegular = count(regularValues);
        // $regularity = round(((averageRegular - (sumAbsDiff / $nbJumps)) * 100) / averageRegular);
    }

    return {
        regularCount,
        unsualCount,
        regularity,
        classification: classification.sort((a, b) => b.percent - a.percent),
    }
}

const getDisciplineIconSrc = (discipline) => {
    if (typeof navigator !== 'undefined' && !navigator.onLine) {
        return ''
    }
    return `/images/gifs/${disciplinesAvailable.includes(discipline) ? discipline : 'default'}.gif`
}

const getDisciplineColors = (discipline) => {
    if (discipline && tailwind.colorExists(`color.discipline.${discipline}.main`)) {
        return [
            tailwind.theme.color.discipline?.[discipline]?.main,
            tailwind.theme.color.discipline?.[discipline]?.secondary,
        ]
    }
    return [tailwind.theme.color.discipline.default.main, tailwind.theme.color.discipline.default.secondary]
}

const getTotalWeight = (horseWeight, riders) => {
    if (typeof riders === 'object' && !Array.isArray(riders) && riders !== null) {
        riders = [riders]
    }

    let totalRidersWeight = 0
    const riderCount = riders?.length ?? 0
    for (let index = 0; index < riderCount; index++) {
        totalRidersWeight += parseInt(riders[index]?.weight) || defaultRiderWeight
    }

    const averageRiderWeight = riderCount > 0 ? Math.round(totalRidersWeight / riderCount) : 0

    return (parseInt(horseWeight) || defaultHorseWeight) + averageRiderWeight
}

export {
    FURLONG_IN_METERS,
    getFasterStride,
    detectFurlongStartTime,
    detectSuggestedFurlongStart,
    getGaitBlockRange,
    calculateFurlongStatistics,
    calculateStatisticsByChunk,
    calculateStatistics,
    colorThresholds,
    equilibriumTresholds,
    healthEventTresholds,
    standardDeviation,
    regularityAndClassification,
    getDisciplineIconSrc,
    getDisciplineColors,
    getTotalWeight,
}
